import { Button, buttonVariants } from "../../components/ui/button";
import BackIcon from "../../components/back-icon";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import { Link, useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { useDoctor } from "../../lib/hooks/doctors/use-doctor";
import { Calendar, CircleUserRound, Clock, Hospital, Video } from "lucide-react";
import { useAppointmentData } from "../../contexts/appointment-data-context";
import { useToast } from "../../components/ui/use-toast";
import { useDoctorSlots } from "../../lib/hooks/doctors/use-doctor-slots";
import { useEffect, useMemo, useState } from "react";
import { Textarea } from "../../components/ui/textarea";
import { useSymptoms } from "../../lib/hooks/doctors/use-symptoms";
import { useAuth } from "../../contexts/auth-context";
import { usePatientsByMobile } from "../../lib/hooks/doctors/use-patients-by-mobile";
import { IPatient } from "../../types";
import Nav from "./nav";
import PopaddPatient from "./pop-add-patient";
import PopverifyPatient from "./pop-verify-patient";
import LargeConfirmAppointment from "./large-confirm-appointment";
import PopAddHealthRecords from "./pop-add-health-records";
import { AppointmentChip } from "../../components/appointment-chip";
import FaqTeleconsultation from "../../components/faq-teleconsultation";
import Spinner from "../../components/ui/spinner";

const MAX_SLOTS = 6;
const MAX_VISIBLE_CLINICS = 1;
const today = format(new Date(), "dd-MM-yyyy");

function DoctorDetails1() {
  const [searchParams] = useSearchParams();
  const initialBackUrl = searchParams.get("backUrl") || "/doctors-list";
  const [backUrl] = useState(initialBackUrl);
  const { mobile, fetched } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const professionalId = params.professionalId?.split("-").pop(); // ✅ extracts correct ID even from slug
  const urlStep = params.step;
  const [step, setStep] = useState(Number(urlStep) || 1);
  const [showModal, setShowModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showAddHealthRecords, setShowAddHealthRecords] = useState(false);
  const [showVerifyPatient, setShowVerifyPatient] = useState(false);
  const [showPatientSelection, setShowPatientSelection] = useState(false);
  const [isDirectNavigation, setIsDirectNavigation] = useState(true);

  const { data, setData } = useAppointmentData();
  const { doctor, isLoading } = useDoctor(professionalId);

  const [selectedBranch, setSelectedBranch] = useState<number | undefined>();

  const selectedBranchInPC = doctor?.branchDetails?.find(
    branch => branch.branchId === selectedBranch
  );

  const currentAppointmentType = (selectedBranchInPC?.isTeleconsultationEnable) ? data?.appointmentType || "TELECONSULTATION" : data?.appointmentType || "WALK_IN";

  useEffect(() => {
    if (!doctor) return;

    const type = selectedBranchInPC?.isTeleconsultationEnable ? "TELECONSULTATION" : "WALK_IN";

    setData({
      patient: data?.patient,
      appointmentType: type,
      slotTiming: data?.slotTiming,
      slotDate: data?.slotDate,
      dayOfWeek: data?.dayOfWeek,
      symptoms: data?.symptoms ?? [],
      healthRecords: data?.healthRecords,
      healthRecordPhysician: data?.healthRecordPhysician,
      branchDetails: data?.branchDetails ?? []
    });

    console.info("Auto selected appointment type:", type);
  }, [doctor]); // Only depend on 'doctor'

  useEffect(() => {
    const directlyAccessedDoctor = !searchParams.get("backUrl") && window.history.length <= 1;
    //console.log("Directly accessed?", directlyAccessedDoctor);
    localStorage.setItem("directAccessedDoctor", String(directlyAccessedDoctor));
    setIsDirectNavigation(directlyAccessedDoctor);
  }, []);


  const seoPath = useMemo(() => {
    if (!doctor) return "";

    const name = doctor.fullName?.toLowerCase().replace(/\s+/g, "-") || "unknown";
    const specialty =
      doctor.specialities
        ?.map((s) => s.toLowerCase().replace(/\s+/g, "-"))
        .join("-") || "general";

    // Get location from first branch
    const branch = doctor.branchDetails?.[0] || {};
    const state = branch.state?.trim() ? branch.state.toLowerCase().replace(/\s+/g, "-") : null;
    const district = branch.district?.trim() ? branch.district.toLowerCase().replace(/\s+/g, "-") : null;
    const city = branch.subDistrictOrCity?.trim() ? branch.subDistrictOrCity.toLowerCase().replace(/\s+/g, "-") : null;

    // Dynamically build location parts based on availability
    const locationParts = [];
    if (state) locationParts.push(state);
    if (district) locationParts.push(district);
    if (city) locationParts.push(city);

    // Build final URL
    const url = `/doctors/${name}-${specialty}${locationParts.length ? `-${locationParts.join("-")}` : ""}-${professionalId}`;

    //console.log("Generated SEO URL:", url);

    return url;
  }, [doctor, professionalId]);

  useEffect(() => {
    if (!isLoading && seoPath && window.location.pathname === `/doctors/${professionalId}`) {
      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.set("backUrl", backUrl);
      navigate(`${seoPath}?${updatedSearchParams.toString()}`, { replace: true });
    }
  }, [isLoading, seoPath, professionalId, navigate]);

  const {
    slots = {},
    bookedSlotsData = { bookedSlots: [], patientPerSlot: 0 },
    isLoading: isSlotsLoading,
    isFetched: isSlotsFetched,
  } = useDoctorSlots(professionalId, selectedBranch);

  const [bookedSlots, setBookedSlots] = useState<any[]>([]);
  const [patientPerSlot, setPatientPerSlot] = useState<number>(0);

  useEffect(() => {
    if (bookedSlotsData) {
      setBookedSlots(bookedSlotsData.bookedSlots);
      setPatientPerSlot(bookedSlotsData.patientPerSlot);
    }
  }, [bookedSlotsData]);

  const isBooked = (slot: any) => {
    // Count the booked slots for the current slot
    const bookedCount = bookedSlots.filter(
      (bSlot) =>
        bSlot.from === slot.from &&
        bSlot.to === slot.to &&
        bSlot.date === selectedDate
    ).length;
    return bookedCount >= patientPerSlot;
  };

  const [selectedDate, setSelectedDate] = useState(today);
  const selectedDateSlots = useMemo(
    () => slots[selectedDate] ?? [],
    [slots, selectedDate],
  );

  const [paginatedSlots, setPaginatedSlots] = useState<
    typeof selectedDateSlots
  >([]);

  const { patients } = usePatientsByMobile({});
  const { symptoms = [], isLoading: isSymptomsLoading } = useSymptoms({
    enabled: !!(doctor?.specialityIds ?? []).length,
    specialityIds: doctor?.specialityIds || [],
  })

  function handleSelectPatient(patient: IPatient) {
    setData({
      ...data,
      patient: {
        id: patient.patientId,
        name: patient.name,
      },
      branchDetails: data?.branchDetails || [],
    });
    // console.log("Selected patient:", patient);
    // console.log("Data after selection:", data);
  }

  function handleTypeSymptoms(symptoms: string) {
    const newSymptoms = symptoms.split(",").map((s) => s.trim());
    setData({
      ...data,
      symptoms: newSymptoms,
      branchDetails: data?.branchDetails || [],
    });
  }

  function handleToggleSelectSymptom(symptom: string) {
    setData({
      ...data,
      symptoms: data?.symptoms?.includes(symptom)
        ? data?.symptoms?.filter((s) => s !== symptom)
        : [...(data?.symptoms || []), symptom],
      branchDetails: data?.branchDetails || [],
    });
  }

  function handleNextStep() {
    if (step === 1) {
      console.log("AppointmentType: ", currentAppointmentType);
      setData({
        ...data,
        appointmentType: currentAppointmentType,
        branchDetails: data?.branchDetails || [],
      });

      const nextStep = mobile || (!mobile && !showPatientSelection) ? step + 1 : 5;
      setStep(nextStep);
      navigate(
        `?step=${nextStep}&backUrl=${encodeURIComponent(backUrl)}`,
      );
    } else if (step === 2 && !data?.slotTiming) {
      toast({
        title: "Please select a slot to continue",
        variant: "destructive",
      });
    } else if (step === 3 && mobile && !data?.patient) {
      toast({
        title: "Please select a patient to continue",
        variant: "destructive",
      });
    } else if (step === 3 && !mobile && showPatientSelection && !data?.patient) {
      toast({
        title: "Please select a patient to continue",
        variant: "destructive",
      });
    } else if (!mobile && step === 3 && !showPatientSelection) {
      setShowVerifyPatient(true);
    } else {
      const nextStep = mobile || (!mobile && !showPatientSelection) ? step + 1 : 5;
      setStep(nextStep);
      navigate(
        `?step=${nextStep}&backUrl=${encodeURIComponent(backUrl)}`,
      );
    }
  }

  const handlePrevStep = () => {
    if (step === 3 && !mobile && showPatientSelection) {
      setShowPatientSelection(false);
      setShowVerifyPatient(false);
    } else if (step === 3 && !mobile && showVerifyPatient) {
      setShowVerifyPatient(false);
    } else {
      setStep((prev) => Math.max(prev - 1, 1));
      navigate(`?step=${Math.max(step - 1, 1)}&backUrl=${encodeURIComponent(backUrl)}`);
    }
  };

  function handleContinue() {
    if (mobile && patients?.length && !data?.patient) {
      return toast({
        title: "Please select a patient to continue",
        variant: "destructive",
      });
    }

    if (mobile && !patients?.length && professionalId) {
      return navigate(
        `/add-patient?redirectUrl=/doctors/${professionalId}/appointment/confirm&backUrl=/doctors/${professionalId}/appointment`,
      );
    }

    if (mobile) {
      setStep(5);
      navigate(
        `/doctors/${doctor?.professionalId}?step=5&backUrl=${encodeURIComponent(backUrl)}`,
      );
    } else {
      const verifyUrl = `/verify-patient?redirect=/doctors/${doctor?.professionalId}/appointment?step=2&showPatientSelection=true&backUrl=${encodeURIComponent(backUrl)}`;
      navigate(verifyUrl);
    }
  }

  // useEffect(() => {
  //   if (
  //     !isLoading &&
  //     (!data?.slotDate || !data.slotTiming || !data.dayOfWeek)
  //   ) {
  //     navigate(`/doctors/${doctor?.professionalId}`);
  //   }
  // }, [data, doctor?.professionalId, navigate, isLoading]);

  useEffect(() => {
    if (patients?.length === 0 && !showSignupModal && mobile) {
      setShowModal(true);
    }
  }, [patients, showSignupModal, mobile]);

  function handleSelectSlot(slot: { from: string; to: string }) {
    const [day, month, year] = selectedDate.split("-").map(Number);
    const jsDate = new Date(year, month - 1, day);

    setData({
      ...data,
      slotTiming: `${slot.from} - ${slot.to}`,
      dayOfWeek: format(jsDate, "EEEE"),
      slotDate: jsDate,
      branchDetails: data?.branchDetails || [],
    });
  }

  useEffect(() => {
    if (isSlotsFetched) {
      setSelectedDate(Object.keys(slots)[0] || today);
    }
  }, [isSlotsFetched, slots]);

  // useEffect(() => {
  //   if (selectedDateSlots.length) {
  //     setPaginatedSlots(selectedDateSlots.slice(0, MAX_SLOTS));
  //   }
  // }, [selectedDateSlots]);

  useEffect(() => {
    if (selectedDateSlots.length) {
      const now = new Date();

      // Filter out past slots first
      const activeSlots = selectedDateSlots.filter((slot) => {
        const [day, month, year] = selectedDate.split("-").map(Number);
        const [time, period] = slot.from.split(" ");
        const [startHour, startMinute] = time.split(":").map(Number);

        const hour24 =
          period.toLowerCase() === "pm" && startHour !== 12
            ? startHour + 12
            : period.toLowerCase() === "am" && startHour === 12
              ? 0
              : startHour;

        const slotStartTime = new Date(
          year,
          month - 1,
          day,
          hour24,
          startMinute
        );

        // Return true only for future slots (not passed)
        return slotStartTime >= now;
      });

      // Then apply pagination to active slots only
      setPaginatedSlots(activeSlots.slice(0, MAX_SLOTS));
    }
  }, [selectedDateSlots, selectedDate]);

  const storedPatient = localStorage.getItem("selectedPatient");
  const [selectedPatient, setSelectedPatient] = useState(storedPatient);

  const [showAllClinics, setShowAllClinics] = useState(false);
  const branchDetails = doctor?.branchDetails || [];
  const visibleBranches = showAllClinics
    ? branchDetails
    : branchDetails.slice(0, MAX_VISIBLE_CLINICS);

  useEffect(() => {
    if (doctor?.branchDetails?.length) {
      const defaultBranch = doctor.branchDetails[0];
      setSelectedBranch(defaultBranch.branchId);
      setData({
        ...data,
        branchDetails: [defaultBranch],
      });
    }
  }, [doctor, setData]);

  const handleBranchSelect = (branchId: number) => {
    const selectedBranch1 = doctor?.branchDetails?.find(
      (branch) => branch.branchId === branchId,
    );

    setSelectedBranch(branchId);

    if (selectedBranch1) {
      setData({
        ...data,
        branchDetails: [selectedBranch1],
      });
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const stepParam = Number(queryParams.get("step"));
    const showPatientSelectionParam = queryParams.get("showPatientSelection");
    if (stepParam) {
      setStep(stepParam);
    }
    if (showPatientSelectionParam === "true" && !mobile) {
      setShowPatientSelection(true);
    }
  }, [location.search]);

  const handleAppointmentTypeClick = (e: React.MouseEvent, type: "WALK_IN" | "TELECONSULTATION") => {
    e.preventDefault();
    e.stopPropagation();

    setData({
      ...data,
      appointmentType: type,
      patient: data?.patient,
      slotTiming: data?.slotTiming,
      slotDate: data?.slotDate,
      dayOfWeek: data?.dayOfWeek,
      symptoms: data?.symptoms ?? [],
      healthRecords: data?.healthRecords,
      healthRecordPhysician: data?.healthRecordPhysician,
      branchDetails: data?.branchDetails ?? []
    });
  };


  if (isLoading || !doctor) {
    return (
      <>
        <div className="fixed top-0 left-0 right-0 z-50  bg-white px-4">
          <Nav
            selectedPatient={selectedPatient}
            setSelectedPatient={setSelectedPatient}
          />
        </div>
        <div className="flex items-center justify-center h-[80vh]">
          <Spinner />
          {/* <Loader /> */}
        </div>
      </>
    );
  }

  return (
    <>
      {/* <div className="fixed top-0 left-0 right-0 z-50  bg-white px-4">
        <Nav
          selectedPatient={selectedPatient}
          setSelectedPatient={setSelectedPatient}
        />
      </div> */}

      <div className="sm:p-4 md:p-4 lg:min-h-screen lg:overflow-hidden lg:px-0 lg:py-0 bg-white lg:bg-[rgb(244,246,250)] lg:mt-0">
        <div className="relative w-full mx-auto lg:px-0">
          <div className="relative group">

            {/* {(localStorage.getItem("directAccessedDoctor") !== "true" || step === 1) && ( */}
            {(!isDirectNavigation || searchParams.get("backUrl") && (Number(searchParams.get("step")) < 2)) && (
              <Link
                to={`/doctors`}
                className={buttonVariants({
                  className: "absolute left-4 top-0 mt-6",
                  variant: "ghost",
                  size: "icon",
                })}
                onClick={() => setData({ branchDetails: [] })}
              >
                <BackIcon />
                <span className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block items-center px-2 py-1 rounded text-xs text-white bg-gray-800 shadow-lg z-50">
                  Go Back
                </span>
              </Link>
            )}
          </div>

          <div className="lg:flex lg:px-0">
            <div
              className="lg:w-3/5 space-y-6 lg:overflow-y-auto lg:h-screen lg:p-10"
              style={{
                overflowY: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <div className="lg:flex">
                <div className="text-center lg:p-4 mt-6">
                  {doctor?.profilePhoto ? (
                    <img
                      src={`${doctor?.profilePhoto}`}
                      alt={doctor?.fullName}
                      className="mx-auto size-28 rounded-full object-contain"
                    />
                  ) : (
                    <div className="mx-auto size-28 rounded-full object-contain">
                      <CircleUserRound
                        className="size-full"
                        strokeWidth={1}
                      />
                    </div>
                  )}
                </div>

                <div className="lg:ml-18 mt-6">
                  <h2 className="text-lg font-bold">Dr {doctor?.fullName}</h2>
                  {doctor?.specialities && doctor.specialities.length > 0 && (
                    <p className="mt-2 text-sm ">
                      {doctor.specialities.join(", ")}
                    </p>
                  )}
                  <p className="mt-2 text-sm">
                    {doctor?.nameOfDegreeOrDiplomaObtained && doctor.nameOfDegreeOrDiplomaObtained.length > 0
                      ? doctor.nameOfDegreeOrDiplomaObtained.join(", ")
                      : ""}
                  </p>
                  <h4 className="mb-2 text-sm font-bold">{doctor?.clinicName}</h4>
                  {/* <p className="text-sm font-bold">
                    Fee: {doctor?.fee == null || Number(doctor?.fee) === 0.0 ? "NA" : `₹${doctor.fee}`}
                  </p> */}
                  <p className="text-sm">{doctor?.subDistrictOrCity === null ? "NA" : doctor?.subDistrictOrCity}</p>
                  <p className="text-sm">{doctor?.state === null ? "NA" : doctor?.state}</p>
                </div>
              </div>

              <div className="my-5 h-1.5 bg-border" />
              <div className="container">
                <div className="mb-6 space-y-4">
                  {visibleBranches.map((branch) => (
                    <div key={branch.branchId}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              name="clinicBranch"
                              checked={selectedBranch === branch.branchId}
                              onChange={() => handleBranchSelect(branch.branchId)}
                              className="mr-2"
                              style={{
                                accentColor: "hsl(var(--primary-2))",
                              }}
                            />
                            <h4 className="text-sm font-bold">
                              {branch.clinicName}
                            </h4>
                          </label>
                          {
                            // Only render if at least one field is non-null
                            (branch?.addressLine1 || branch?.addressLine2 || branch?.state || branch?.pincode) && (
                              <>
                                {
                                  // Render first <p> only if addressLine1 or addressLine2 is non-null
                                  (branch?.addressLine1 || branch?.addressLine2) && (
                                    <p className="text-sm ml-6">
                                      {[
                                        branch?.addressLine1,
                                        branch?.addressLine2
                                      ].filter(Boolean).join(", ")}
                                    </p>
                                  )
                                }
                                {
                                  // Render <p> only if at least one location field is non-null
                                  (branch?.state || branch?.district || branch?.subDistrictOrCity || branch?.pincode) && (
                                    <p className="text-sm ml-6">
                                      {[
                                        branch?.state,
                                        branch?.district,
                                        branch?.subDistrictOrCity,
                                        branch?.pincode
                                      ].filter(Boolean).join(", ")}
                                    </p>
                                  )
                                }
                              </>
                            )
                          }
                        </div>
                        {branch?.fee != null && Number(branch.fee) !== 0 && (
                          <p className="text-sm font-bold">
                            Fee : ₹{branch.fee}
                          </p>
                        )}

                      </div>
                    </div>
                  ))}
                  {branchDetails.length > MAX_VISIBLE_CLINICS &&
                    !showAllClinics && (
                      <Button
                        variant="link"
                        className="!mt-0 p-0 text-xs font-bold text-primary underline"
                        onClick={() => setShowAllClinics(true)}
                      >
                        See more clinics
                      </Button>
                    )}
                </div>
              </div>

              <div className="space-y-6">
                {doctor?.aboutDoctor && doctor.aboutDoctor.trim() !== "" && (
                  <div className="lg:p-4 lg:border lg:border-gray-200 lg:rounded-lg lg:shadow lg:bg-white">
                    <h2 className="mb-2 text-lg font-bold">About Doctor</h2>
                    <span>
                      {doctor?.aboutDoctor == null || doctor.aboutDoctor.trim() === "" ? (
                        <li className="text-sm">
                          NA
                        </li>
                      ) : (
                        doctor.aboutDoctor.split('\n').map((line, index) => (
                          <span key={index}>
                            {line}
                            <br />
                          </span>
                        ))
                      )}
                    </span>
                  </div>
                )}

                {/* <div className="lg:p-4 lg:border lg:border-gray-200 lg:rounded-lg lg:shadow lg:bg-white">
                  <h2 className="mb-2 font-bold">Services</h2>
                  <ul className="list-disc space-y-2 pl-4 text-gray-600">
                    {doctor?.services?.length ? (
                      doctor.services.map((service) => (
                        <li key={service} className="text-sm">
                          {service}
                        </li>
                      ))
                    ) : (
                      <li className="text-sm">No services listed</li>
                    )}
                  </ul>
                </div> */}

                <div className="lg:p-4 lg:border lg:border-gray-200 lg:rounded-lg lg:shadow lg:bg-white">
                  <h2 className="mb-2 font-bold">Specialisations</h2>
                  <ul className="list-disc space-y-2 pl-4 text-gray-600">
                    {doctor?.specialities?.length ? (
                      doctor.specialities.map((speciality) => (
                        <li key={speciality} className="text-sm">
                          {speciality}
                        </li>
                      ))
                    ) : (
                      <li className="text-sm">No specialities listed</li>
                    )}
                  </ul>
                </div>
                {Number(doctor?.yearOfExperience) > 0 && (
                  <div className="lg:p-4 lg:border lg:border-gray-200 lg:rounded-lg lg:shadow lg:bg-white">
                    <h2 className="mb-2 font-bold">Experience</h2>
                    <ul className="list-disc space-y-2 pl-4 text-gray-600">
                      <li key={doctor?.yearOfExperience} className="text-sm">
                        {doctor?.yearOfExperience} years
                      </li>
                    </ul>
                  </div>)}



                {doctor && Array.isArray(doctor?.nameOfDegreeOrDiplomaObtained) && doctor.nameOfDegreeOrDiplomaObtained.length > 0 && (
                  <div className="lg:p-4 lg:border lg:border-gray-200 lg:rounded-lg lg:shadow lg:bg-white">
                    <h2 className="mb-2 font-bold">Education</h2>
                    <ul className="list-disc space-y-2 pl-4 text-gray-600">
                      {doctor?.nameOfDegreeOrDiplomaObtained && doctor.nameOfDegreeOrDiplomaObtained.length > 0 ? (
                        doctor.nameOfDegreeOrDiplomaObtained.map((degree) => (
                          <li key={degree} className="text-sm">
                            {degree}
                          </li>
                        ))
                      ) : (
                        <li className="text-sm">NA</li>
                      )}
                    </ul>
                  </div>
                )}
                {doctor && Array.isArray(doctor?.awardAndRecognition) && doctor.awardAndRecognition.length > 0 && (
                  <div className="lg:p-4 lg:border lg:border-gray-200 lg:rounded-lg lg:shadow lg:bg-white">
                    <h2 className="mb-2 font-bold">Rewards and Recognition</h2>
                    <ul className="list-disc space-y-2 pl-4 text-gray-600">
                      {doctor?.awardAndRecognition?.length ? (
                        doctor.awardAndRecognition.map((awd) => (
                          <li key={awd} className="text-sm">
                            {awd}
                          </li>
                        ))
                      ) : (
                        <li className="text-sm">
                          No rewards and recognition listed
                        </li>
                      )}
                    </ul>
                  </div>
                )}
                {selectedBranchInPC?.isTeleconsultationEnable && <div className="mt-8">
                  <FaqTeleconsultation />
                </div>}
              </div>
            </div>

            <div
              className="lg:flex-1 lg:bg-white lg:w-2/5 lg:shadow-xl flex flex-col items-center"
              style={{
                overflowY: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <div
                className="lg:overflow-y-auto w-full max-w-lg flex flex-col h-[100vh] p-0"
                style={{
                  overflowY: "scroll",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                <style>
                  {`
                    /* Hide scrollbar for Chrome, Safari, and Opera */
                    .lg\\:overflow-y-auto::-webkit-scrollbar {
                      display: none;
                    }

                    /* Hide scrollbar for Edge and Internet Explorer */
                    .lg\\:overflow-y-auto {
                      -ms-overflow-style: none;
                      scrollbar-width: none;
                    }
                  `}
                </style>
                <div className="lg:font-bold lg:text-2xl text-center mt-20 pb-2 mb-4 border-b-4 border-gray-200">
                  Book appointment
                </div>

                {step === 1 && (
                  <div className="flex flex-col items-center lg:mb-44 px-6">
                    <h2 className="font-bold text-center pb-6 text-lg">
                      Select Appointment Type
                    </h2>

                    {/* Appointment Type Selection */}
                    <div className="flex items-center justify-center gap-4 flex-wrap max-sm:gap-3 mb-8">
                      {selectedBranchInPC?.isInClinicConsultationEnable && <AppointmentChip
                        type="WALK_IN"
                        icon={<Hospital className="w-4 h-4 max-sm:w-3 max-sm:h-3" />}
                        label="In-clinic Appointment"
                        shortLabel="Walk-in"
                        isSelected={currentAppointmentType === "WALK_IN"}
                        onClick={handleAppointmentTypeClick}
                      />}

                      {selectedBranchInPC?.isTeleconsultationEnable && <AppointmentChip
                        type="TELECONSULTATION"
                        icon={<Video className="w-4 h-4 max-sm:w-3 max-sm:h-3" />}
                        label="Teleconsultation"
                        shortLabel="Video Call"
                        isSelected={currentAppointmentType === "TELECONSULTATION"}
                        onClick={handleAppointmentTypeClick}
                      />}

                      {!(selectedBranchInPC?.isTeleconsultationEnable) && !(selectedBranchInPC?.isInClinicConsultationEnable) && (
                        <div className="flex items-center justify-center">
                          <p>Not Available Now</p>
                        </div>
                      )}
                    </div>

                    {/* Appointment Type Descriptions */}
                    <div className="w-full max-w-md space-y-6 mb-8">
                      {/* Walk-in Description */}
                      {selectedBranchInPC?.isInClinicConsultationEnable && <div className={`p-4 rounded-lg border-2 transition-all duration-200 ${currentAppointmentType === "WALK_IN"
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-gray-200 bg-gray-50'
                        }`}>
                        <div className="flex items-start gap-3">
                          <Hospital className={`w-5 h-5 mt-0.5 flex-shrink-0 ${currentAppointmentType === "WALK_IN" ? 'text-primary' : 'text-gray-600'
                            }`} />
                          <div>
                            <h3 className="font-semibold text-sm mb-1">In-clinic Appointment</h3>
                            <p className="text-xs text-gray-600 leading-relaxed">
                              Visit the clinic in-person for comprehensive examination and
                              hands-on medical care with direct doctor-patient interaction.
                            </p>
                          </div>
                        </div>
                      </div>}

                      {/* Teleconsultation Description */}
                      {selectedBranchInPC?.isTeleconsultationEnable && <div className={`p-4 rounded-lg border-2 transition-all duration-200 ${currentAppointmentType === "TELECONSULTATION"
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-gray-200 bg-gray-50'
                        }`}>
                        <div className="flex items-start gap-3">
                          <Video className={`w-5 h-5 mt-0.5 flex-shrink-0 ${currentAppointmentType === "TELECONSULTATION" ? 'text-primary' : 'text-gray-600'
                            }`} />
                          <div>
                            <h3 className="font-semibold text-sm mb-1">Teleconsultation</h3>
                            <p className="text-xs text-gray-600 leading-relaxed">
                              Consult with doctor from home through secure video call.
                              Save time and costs while maintaining quality healthcare service.
                            </p>
                          </div>
                        </div>
                      </div>}
                    </div>

                    <div className="fixed bottom-0 right-0 w-2/5 bg-white py-4 px-6 shadow-md flex justify-end">
                      <Button onClick={handleNextStep} disabled={!(selectedBranchInPC?.isTeleconsultationEnable) && !(selectedBranchInPC?.isInClinicConsultationEnable)}>Next</Button>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="flex flex-col items-center lg:mb-44">
                    <h2 className="font-bold text-center pb-10">
                      Select a date and time
                    </h2>
                    <Tabs
                      value={selectedDate}
                      onValueChange={(val) => {
                        setSelectedDate(val);
                        setData({
                          ...data,
                          branchDetails: data?.branchDetails ?? [],
                          slotTiming: "",
                          dayOfWeek: "",
                          slotDate: new Date(),
                        });
                      }}
                    >
                      <TabsList className="mb-4 h-[unset] w-full justify-center overflow-x-auto rounded-none border-b-2 p-0">
                        {Object.keys(slots).map((slot) => {
                          const today = format(new Date(), "dd-MM-yyyy");
                          const [day, month, year] = slot.split("-").map(Number);
                          const jsDate = new Date(year, month - 1, day);
                          const isToday = slot === today;
                          const dateStr = isToday
                            ? "Today"
                            : format(jsDate, "dd MMM");

                          return (
                            <TabsTrigger
                              key={slot}
                              value={slot}
                              className="rounded-t-md !border-0 text-sm font-bold text-foreground data-[state=active]:bg-primary-2 data-[state=active]:text-primary-foreground"
                            >
                              {dateStr}
                            </TabsTrigger>
                          );
                        })}
                      </TabsList>

                      <TabsContent
                        value={selectedDate}
                        className="flex flex-col items-center"
                      >
                        {(() => {
                          const now = new Date();
                          const today = format(new Date(), "dd-MM-yyyy");
                          const tomorrow = format(new Date(Date.now() + 24 * 60 * 60 * 1000), "dd-MM-yyyy");

                          // Filter active slots (future slots only)
                          const activeSlots = selectedDateSlots.filter((slot) => {
                            const [day, month, year] = selectedDate.split("-").map(Number);
                            const [time, period] = slot.from.split(" ");
                            const [startHour, startMinute] = time.split(":").map(Number);

                            const hour24 =
                              period.toLowerCase() === "pm" && startHour !== 12
                                ? startHour + 12
                                : period.toLowerCase() === "am" && startHour === 12
                                  ? 0
                                  : startHour;

                            const slotStartTime = new Date(
                              year,
                              month - 1,
                              day,
                              hour24,
                              startMinute
                            );

                            return slotStartTime >= now;
                          });

                          if (!(selectedBranchInPC?.isTeleconsultationEnable) && !(selectedBranchInPC?.isInClinicConsultationEnable)) {
                            return (
                              <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                  <Calendar className="w-8 h-8 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                  No appointments available
                                </h3>
                                {/* <p className="text-sm text-gray-500 max-w-sm">
                                  {isToday
                                    ? "No appointment slots are available for today."
                                    : isTomorrow
                                      ? "No appointment slots are available for tomorrow."
                                      : "No appointment slots are available for this date."
                                  }
                                  <br />
                                  Please select another date to view available time slots.
                                </p> */}
                              </div>
                            );
                          }

                          // If no slots exist for this date at all
                          if (selectedDateSlots.length === 0) {
                            const isToday = selectedDate === today;
                            const isTomorrow = selectedDate === tomorrow;

                            return (
                              <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                  <Calendar className="w-8 h-8 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                  No appointments available
                                </h3>
                                <p className="text-sm text-gray-500 max-w-sm pb-28">
                                  {isToday
                                    ? "No appointment slots are available for today."
                                    : isTomorrow
                                      ? "No appointment slots are available for tomorrow."
                                      : "No appointment slots are available for this date."
                                  }
                                  <br />
                                  Please select another date to view available time slots.
                                </p>
                              </div>
                            );
                          }

                          // If slots existed but all are in the past (no active slots remaining)
                          if (activeSlots.length === 0) {
                            const isToday = selectedDate === today;
                            const isTomorrow = selectedDate === tomorrow;

                            return (
                              <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                                  <Clock className="w-8 h-8 text-orange-500" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                  {isToday
                                    ? "No slots remaining for today"
                                    : isTomorrow
                                      ? "No slots remaining for tomorrow"
                                      : "No slots remaining for this date"
                                  }
                                </h3>
                                <p className="text-sm text-gray-500 max-w-sm pb-28">
                                  {isToday
                                    ? "All appointment slots for today have passed or are no longer available."
                                    : isTomorrow
                                      ? "All appointment slots for tomorrow are no longer available."
                                      : "All appointment slots for this date are no longer available."
                                  }
                                  <br />
                                  Please choose another date to find available slots.
                                </p>
                              </div>
                            );
                          }

                          // If we have active slots, show them
                          return (
                            <>
                              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3">
                                {paginatedSlots.map(slot => {
                                  const isSelected = data?.slotTiming === `${slot.from} - ${slot.to}`;
                                  return (
                                    <Button
                                      key={slot.from}
                                      variant="outline"
                                      size="sm"
                                      className={cn(
                                        "rounded-full border-2 text-xs lowercase",
                                        isSelected && "border-primary-2 !bg-primary-2 font-bold !text-primary-foreground"
                                      )}
                                      onClick={() => handleSelectSlot(slot)}
                                      disabled={isBooked(slot)}
                                    >
                                      {slot.from} - {slot.to}
                                    </Button>
                                  );
                                })
                                }
                              </div>

                              {/* Show "See all slots" button if there are more active slots */}
                              {activeSlots.length > MAX_SLOTS && paginatedSlots.length <= MAX_SLOTS && (
                                <Button
                                  className="mt-7 text-xs font-bold text-primary underline"
                                  variant="link"
                                  onClick={() => setPaginatedSlots([...activeSlots])}
                                >
                                  See all slots ({activeSlots.length} available)
                                </Button>
                              )}

                            </>
                          );
                        })()}
                      </TabsContent>
                    </Tabs>
                    <div className="fixed bottom-0 right-0 w-2/5 bg-white py-4 px-6 shadow-md flex justify-between">
                      <Button variant="outline" onClick={handlePrevStep}>Back</Button>
                      <Button onClick={handleNextStep}>Next</Button>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <>
                    {mobile && !showModal && !showSignupModal ? (
                      <div className="flex flex-col items-center lg:mb-52">
                        <div className="mb-8 w-full">
                          <div className="mb-4 flex items-center gap-2">
                            <h2 className="text-left font-bold">
                              Select patient
                            </h2>
                          </div>
                          {isLoading ? (
                            <div className="flex justify-start mt-4">
                              <p>Loading patients...</p>
                            </div>
                          ) : (
                            <div className="relative">
                              <div className="flex flex-wrap gap-4 justify-start max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                                {patients?.slice(0, 10).map((patient) => {
                                  if (!patient.name) return null;
                                  const isSelected =
                                    data?.patient?.id === patient.patientId;
                                  return (
                                    <Button
                                      key={patient.patientId}
                                      variant="outline"
                                      className={cn("rounded-full border-2", {
                                        "border-primary-2 !bg-primary-2 font-bold !text-primary-foreground":
                                          isSelected,
                                      })}
                                      onClick={() =>
                                        handleSelectPatient(patient)
                                      }
                                    >
                                      {patient.name}
                                    </Button>
                                  );
                                })}
                                <button
                                  onClick={() => {
                                    if (!mobile) {
                                      setShowSignupModal(true);
                                    } else {
                                      setShowModal(true);
                                    }
                                  }}
                                  className="px-4 py-2 rounded-full border border-gray-300 bg-gray-100 hover:bg-gray-200 text-sm font-bold flex items-center justify-center cursor-pointer relative z-10"
                                >
                                  + Add New
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="fixed bottom-0 right-0 w-2/5 bg-white py-4 px-6 shadow-md flex justify-between">
                          <Button variant="outline" onClick={handlePrevStep}>Back</Button>
                          <Button onClick={handleNextStep}>Next</Button>
                        </div>
                      </div>
                    ) : !mobile && !showVerifyPatient && !showPatientSelection ? (
                      <div
                        className="flex flex-col items-center lg:pb-32"
                        style={{
                          overflowY: "auto",
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                        }}
                      >
                        <h2 className="my-4 text-left font-bold">
                          Add Symptoms
                        </h2>
                        <Textarea
                          placeholder="Type your symptoms here or select from the list below"
                          rows={5}
                          value={data?.symptoms?.join(", ")}
                          onChange={(e) => handleTypeSymptoms(e.target.value)}
                        />
                        {isSymptomsLoading ? (
                          <p>Loading...</p>
                        ) : (
                          !!symptoms.length && (
                            <ul className="mt-6 flex flex-wrap gap-x-4 gap-y-3">
                              {symptoms.map((symptom) => {
                                const isSelected =
                                  data?.symptoms?.includes(symptom);
                                return (
                                  <li
                                    key={symptom}
                                    className={cn(
                                      "cursor-pointer rounded-full border px-4 py-2 text-xs",
                                      {
                                        "border-primary-2 bg-primary-2 text-primary-foreground":
                                          isSelected,
                                      },
                                    )}
                                    onClick={() =>
                                      handleToggleSelectSymptom(symptom)
                                    }
                                  >
                                    {symptom}
                                  </li>
                                );
                              })}
                            </ul>
                          )
                        )}
                        <div className="fixed bottom-0 right-0 w-2/5 bg-white py-4 px-6 shadow-md flex justify-between">
                          <Button variant="outline" onClick={handlePrevStep}>Back</Button>
                          <Button onClick={handleNextStep}>Next</Button>
                        </div>
                      </div>
                    ) : !mobile && showVerifyPatient ? (
                      <div className="mt-6 w-full">
                        <PopverifyPatient
                          onClose={() => {
                            setShowVerifyPatient(false);
                            setShowPatientSelection(false);
                            setStep(2);
                            navigate(
                              `/doctors/${professionalId}?step=2&backUrl=${encodeURIComponent(backUrl)}`
                            );
                          }}
                        />
                      </div>
                    ) : !mobile && showPatientSelection ? (
                      <div className="flex flex-col items-center lg:mb-52">
                        <div className="mb-8 w-full">
                          <div className="mb-4 flex items-center gap-2">
                            <h2 className="text-left font-bold">
                              Select patient
                            </h2>
                          </div>
                          {isLoading ? (
                            <div className="flex justify-start mt-4">
                              <p>Loading patients...</p>
                            </div>
                          ) : (
                            <div className="relative">
                              <div className="flex flex-wrap gap-4 justify-start max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                                {patients?.slice(0, 10).map((patient) => {
                                  if (!patient.name) return null;
                                  const isSelected =
                                    data?.patient?.id === patient.patientId;
                                  return (
                                    <Button
                                      key={patient.patientId}
                                      variant="outline"
                                      className={cn("rounded-full border-2", {
                                        "border-primary-2 !bg-primary-2 font-bold !text-primary-foreground":
                                          isSelected,
                                      })}
                                      onClick={() =>
                                        handleSelectPatient(patient)
                                      }
                                    >
                                      {patient.name}
                                    </Button>
                                  );
                                })}
                                <button
                                  onClick={() => setShowModal(true)}
                                  className="px-4 py-2 rounded-full border border-gray-300 bg-gray-100 hover:bg-gray-200 text-sm font-bold flex items-center justify-center cursor-pointer relative z-10"
                                >
                                  + Add New
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="fixed bottom-0 right-0 w-2/5 bg-white py-4 px-6 shadow-md flex justify-between">
                          <Button variant="outline" onClick={handlePrevStep}>Back</Button>
                          <Button onClick={handleNextStep}>Next</Button>
                        </div>
                      </div>
                    ) : null}
                  </>
                )}

                {step === 3 && showModal && (
                  <div className="w-full">
                    <PopaddPatient
                      onClose={(newPatient?: { patientId: string; name: string }) => {
                        setShowModal(false);
                        if (newPatient?.patientId && newPatient.name) {
                          handleSelectPatient({
                            patientId: newPatient.patientId,
                            name: newPatient.name,
                          } as unknown as IPatient); // Safer cast
                        }
                      }}
                    />
                  </div>
                )}
                {step === 4 && mobile && (
                  <div
                    className="flex flex-col items-center lg:pb-32"
                    style={{
                      overflowY: "auto",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                    }}
                  >
                    <h2 className="my-4 text-left font-bold">Add Symptoms</h2>
                    <Textarea
                      placeholder="Type your symptoms here or select from the list below"
                      rows={5}
                      value={data?.symptoms?.join(", ")}
                      onChange={(e) => handleTypeSymptoms(e.target.value)}
                    />
                    {isSymptomsLoading ? (
                      <p>Loading...</p>
                    ) : (
                      !!symptoms.length && (
                        <ul className="mt-6 flex flex-wrap gap-x-4 gap-y-3">
                          {symptoms.map((symptom) => {
                            const isSelected = data?.symptoms?.includes(symptom);
                            return (
                              <li
                                key={symptom}
                                className={cn(
                                  "cursor-pointer rounded-full border px-4 py-2 text-xs",
                                  {
                                    "border-primary-2 bg-primary-2 text-primary-foreground":
                                      isSelected,
                                  },
                                )}
                                onClick={() => handleToggleSelectSymptom(symptom)}
                              >
                                {symptom}
                              </li>
                            );
                          })}
                        </ul>
                      )
                    )}
                    <div className="fixed bottom-0 right-0 w-2/5 bg-white py-4 px-6 shadow-md flex justify-between">
                      <Button variant="outline" onClick={handlePrevStep}>Back</Button>
                      <Button onClick={handleContinue}>Next</Button>
                    </div>
                  </div>
                )}

                {step === 5 && (
                  <div className="flex flex-col items-center w-full lg:mb-44">
                    {showAddHealthRecords ? (
                      <PopAddHealthRecords
                        onClose={() => setShowAddHealthRecords(false)}
                      />
                    ) : (
                      <LargeConfirmAppointment
                        onAddHealthRecords={() =>
                          setShowAddHealthRecords(true)
                        }
                      />
                    )}
                    <div className="fixed bottom-0 right-0 w-2/5 bg-white py-4 px-6 shadow-md flex justify-between">
                      <Button variant="outline" onClick={handlePrevStep}>Back</Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default DoctorDetails1;